import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ApiUrl } from '../constants/api-url';
import { getRoleContext, userRoleContext } from '../constants/role-details.constants';
import { catchError, forkJoin, map, of } from 'rxjs';

export interface Banner {
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  uniqueID: string;
  bannerType: string;
}

@Injectable({
  providedIn: 'root'
})
export class BannerService {
  private httpClient = inject(HttpClient);

  getActiveBanners() {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    // SM-3426: The Buyer banner endpoints are gated to UserRoleContext.Buyer in obm-core, so
    // Supplier (SEM/LMC) logins must hit the Supplier-area equivalents instead (otherwise 403).
    // Both roles fetch Site + Maintenance banners, matching the legacy behavior.
    const isSupplier = getRoleContext() === userRoleContext.Supplier;
    const siteBannersUrl = isSupplier ? ApiUrl.SUPPLIER_GET_ACTIVE_SITE_BANNERS : ApiUrl.GET_ACTIVE_SITE_BANNERS;
    const maintenanceBannersUrl = isSupplier ? ApiUrl.SUPPLIER_GET_ACTIVE_BANNERS : ApiUrl.GET_ACTIVE_BANNERS;

    // Fetch both Site banners (admin-created announcements) and Maintenance banners.
    // The Buyer endpoints return an array; the Supplier endpoints return a single banner
    // (or null) via FirstOrDefault, so normalize both shapes to a Banner[] before merging.
    const siteBanners$ = this.httpClient.get<Banner[] | Banner | null>(siteBannersUrl, { headers }).pipe(
      map(res => this.toBannerArray(res)),
      catchError(() => of<Banner[]>([]))
    );
    const maintenanceBanners$ = this.httpClient.get<Banner[] | Banner | null>(maintenanceBannersUrl, { headers }).pipe(
      map(res => this.toBannerArray(res)),
      catchError(() => of<Banner[]>([]))
    );

    return forkJoin([siteBanners$, maintenanceBanners$]).pipe(
      map(([siteBanners, maintenanceBanners]) => [...siteBanners, ...maintenanceBanners])
    );
  }

  private toBannerArray(res: Banner[] | Banner | null | undefined): Banner[] {
    if (!res) return [];
    return Array.isArray(res) ? res : [res];
  }
}
